/**
 * Privacy Policy Screen
 * Politique de Confidentialité — conforme RGPD art. 13
 * URL: https://wealfi.co/fr/privacy
 */

import React from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  Pressable,
  useWindowDimensions,
  Platform,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import useDomain from '../../hooks/useDomain';
import useWebSEO from '../../hooks/useWebSEO';

const brandColors = {
  green: '#71B352',
  greenDark: '#5B8A3C',
  greenLight: '#E8F5E0',
  dark: '#3D4543',
  orange: '#D97234',
  orangeLight: '#FFF3EB',
  gray: '#6B7280',
  background: '#FAFBFC',
  white: '#FFFFFF',
};

// ---- Data tables ----

const dataCollectedRows = [
  {
    category: 'Identité',
    data: 'Nom, prénom, adresse email',
    source: 'Inscription',
  },
  {
    category: 'Financières déclaratives',
    data: 'Revenus, charges, épargne, objectifs',
    source: 'Saisie manuelle',
  },
  {
    category: 'Conversations IA',
    data: 'Échanges avec l’assistant',
    source: 'Utilisation du service',
  },
  {
    category: 'Données bancaires',
    data: 'Soldes, transactions, catégories',
    source: 'Connexion Powens (optionnel)',
  },
  {
    category: 'Navigation et usage',
    data: 'Pages visitées, actions, appareil, IP',
    source: 'Automatique',
  },
  {
    category: 'Paiement',
    data: 'Informations de transaction',
    source: 'Stripe (nous ne stockons pas tes données de carte)',
  },
];

const legalBasisRows = [
  {
    purpose: 'Fourniture du service',
    basis: 'Exécution du contrat (art. 6.1.b RGPD)',
  },
  {
    purpose: 'Amélioration du service',
    basis: 'Intérêt légitime (art. 6.1.f RGPD)',
  },
  {
    purpose: 'Analytics',
    basis: 'Consentement (art. 6.1.a RGPD)',
  },
  {
    purpose: 'Sécurité',
    basis: 'Intérêt légitime (art. 6.1.f RGPD)',
  },
  {
    purpose: 'Obligations légales',
    basis: 'Obligation légale (art. 6.1.c RGPD)',
  },
  {
    purpose: 'Connexion bancaire',
    basis: 'Consentement explicite (art. 6.1.a RGPD)',
  },
];

const subProcessorRows = [
  {
    name: 'Powens SAS',
    purpose: 'Agrégation bancaire (ACPR n°16948)',
    data: 'Identifiants bancaires (chiffrés), transactions',
    location: 'France / UE',
  },
  {
    name: 'Stripe Inc.',
    purpose: 'Paiement sécurisé',
    data: 'Données de transaction',
    location: 'UE / États-Unis (clauses contractuelles types)',
  },
  {
    name: 'Hébergeur cloud',
    purpose: 'Hébergement de l’application',
    data: 'Toutes les données',
    location: 'À confirmer',
  },
  {
    name: 'Anthropic',
    purpose: 'Fournisseur d’IA (génération de contenus)',
    data: 'Messages de conversation (anonymisés)',
    location: 'États-Unis (clauses contractuelles types)',
  },
];

export default function PrivacyPolicyScreen() {
  const navigation = useNavigation<any>();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 768;
  const { isLandingDomain } = useDomain();

  useWebSEO({
    title: 'Politique de Confidentialité — Wealfi | RGPD',
    description: 'Politique de confidentialité de Wealfi conforme au RGPD. Données collectées, droits d\'accès, sous-traitants (Powens, Stripe, Anthropic), durée de conservation.',
    keywords: 'politique confidentialité, RGPD, protection données personnelles, Wealfi vie privée',
    ogUrl: 'https://wealfi.co/fr/privacy',
  });

  const handleGoBack = () => {
    if (Platform.OS === 'web' && isLandingDomain) {
      window.location.href = '/fr';
      return;
    }
    navigation.navigate('Landing' as any);
  };

  return (
    <View style={styles.container}>
      {/* Fixed Navbar */}
      <View style={[styles.navbar, isDesktop && styles.navbarDesktop]}>
        <Pressable onPress={handleGoBack} style={styles.backButton}>
          <Text style={styles.backButtonText}>← Retour</Text>
        </Pressable>
        <View style={styles.logoContainer}>
          <Text style={styles.logoText}>
            Wealfi
          </Text>
        </View>
        {/* Spacer to balance the navbar */}
        <View style={styles.backButton} />
      </View>

      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        <View style={[styles.content, isDesktop && { maxWidth: 800, alignSelf: 'center' as const, width: '100%' as const }]}>

          {/* Page Title */}
          <Text style={styles.pageTitle}>Politique de Confidentialité</Text>
          <Text style={styles.updateDate}>Dernière mise à jour : 24 février 2026</Text>

          {/* Section 1 */}
          <View style={styles.section}>
            <Text style={styles.heading}>1. Responsable de traitement</Text>
            <Text style={styles.paragraph}>
              Le responsable du traitement de tes données personnelles est :
            </Text>
            <Text style={styles.paragraph}>
              Boubacar Diallo{'\n'}Email : contact@wealfi.co
            </Text>
            <Text style={styles.paragraph}>
              Wealfi est un service édité par Boubacar Diallo, en cours de constitution en société (SAS). Les présentes seront mises à jour lors de l'immatriculation.
            </Text>
          </View>

          {/* Section 2 */}
          <View style={styles.section}>
            <Text style={styles.heading}>2. Données collectées</Text>
            <Text style={styles.paragraph}>
              Nous collectons les catégories de données suivantes :
            </Text>
            <View style={styles.tableContainer}>
              {/* Header */}
              <View style={[styles.tableRow, { backgroundColor: brandColors.greenLight }]}>
                <Text style={[styles.tableCell, styles.tableCellHeader, { flex: 1 }]}>Catégorie</Text>
                <Text style={[styles.tableCell, styles.tableCellHeader, { flex: 2 }]}>Données</Text>
                <Text style={[styles.tableCell, styles.tableCellHeader, { flex: 1.5 }]}>Source</Text>
              </View>
              {dataCollectedRows.map((row, index) => (
                <View
                  key={index}
                  style={[
                    styles.tableRow,
                    index % 2 === 0 ? undefined : styles.tableRowAlt,
                  ]}
                >
                  <Text style={[styles.tableCell, styles.tableCellLabel, { flex: 1 }]}>{row.category}</Text>
                  <Text style={[styles.tableCell, styles.tableCellValue, { flex: 2 }]}>{row.data}</Text>
                  <Text style={[styles.tableCell, styles.tableCellValue, { flex: 1.5 }]}>{row.source}</Text>
                </View>
              ))}
            </View>
          </View>

          {/* Section 3 */}
          <View style={styles.section}>
            <Text style={styles.heading}>3. Finalités du traitement</Text>
            <Text style={styles.paragraph}>
              Tes données sont traitées pour les finalités suivantes :
            </Text>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Fourniture et personnalisation du service (diagnostic IA, assistant, objectifs)</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Amélioration continue du service et de la qualité des analyses IA</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Analyses statistiques et analytics (anonymisées)</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Sécurité du service et prévention de la fraude</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Respect de nos obligations légales et réglementaires</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Communication relative au service (mises à jour, alertes)</Text>
            </View>
          </View>

          {/* Section 4 */}
          <View style={styles.section}>
            <Text style={styles.heading}>4. Bases légales</Text>
            <View style={styles.tableContainer}>
              {/* Header */}
              <View style={[styles.tableRow, { backgroundColor: brandColors.greenLight }]}>
                <Text style={[styles.tableCell, styles.tableCellHeader, { flex: 1 }]}>Finalité</Text>
                <Text style={[styles.tableCell, styles.tableCellHeader, { flex: 2 }]}>Base légale</Text>
              </View>
              {legalBasisRows.map((row, index) => (
                <View
                  key={index}
                  style={[
                    styles.tableRow,
                    index % 2 === 0 ? undefined : styles.tableRowAlt,
                  ]}
                >
                  <Text style={[styles.tableCell, styles.tableCellLabel, { flex: 1 }]}>{row.purpose}</Text>
                  <Text style={[styles.tableCell, styles.tableCellValue, { flex: 2 }]}>{row.basis}</Text>
                </View>
              ))}
            </View>
          </View>

          {/* Section 5 */}
          <View style={styles.section}>
            <Text style={styles.heading}>5. Sous-traitants</Text>
            <Text style={styles.paragraph}>
              Nous faisons appel aux sous-traitants suivants :
            </Text>
            <View style={styles.tableContainer}>
              {subProcessorRows.map((row, index) => (
                <View
                  key={index}
                  style={[
                    styles.tableRow,
                    index % 2 === 0 ? undefined : styles.tableRowAlt,
                    { flexDirection: 'column' as const, alignItems: 'stretch' as const, paddingVertical: 12 },
                  ]}
                >
                  <Text style={[styles.tableCell, styles.tableCellLabel, { marginBottom: 4 }]}>{row.name}</Text>
                  <Text style={[styles.tableCell, styles.tableCellValue]}>{row.purpose}</Text>
                  <Text style={[styles.tableCell, styles.tableCellValue]}>Données partagées : {row.data}</Text>
                  <Text style={[styles.tableCell, styles.tableCellValue]}>Localisation : {row.location}</Text>
                </View>
              ))}
            </View>
          </View>

          {/* Section 6 */}
          <View style={styles.section}>
            <Text style={styles.heading}>6. Durée de conservation</Text>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Données du compte : durée de vie du compte + 3 ans après suppression</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Données de conversation IA : durée de vie du compte + 1 an après suppression</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Données bancaires : durée de la connexion active + 1 an après révocation</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Données de paiement : 10 ans (obligations comptables)</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Logs de sécurité : 1 an</Text>
            </View>
            <Text style={styles.paragraph}>
              Au-delà de ces durées, les données sont supprimées ou anonymisées de manière irréversible.
            </Text>
          </View>

          {/* Section 7 */}
          <View style={styles.section}>
            <Text style={styles.heading}>7. Sécurité des données</Text>
            <Text style={styles.paragraph}>
              Nous mettons en œuvre les mesures suivantes pour protéger tes données :
            </Text>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Chiffrement SSL/TLS pour toutes les communications (données en transit)</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Authentification sécurisée (tokens JWT, liens magiques)</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Accès restreint aux données (principe du moindre privilège)</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Surveillance et journalisation des accès</Text>
            </View>
            <Text style={styles.paragraph}>
              Note de transparence : le chiffrement des données au repos (at-rest encryption) est en cours de déploiement. Nous nous engageons à renforcer continuellement la sécurité de nos systèmes.
            </Text>
          </View>

          {/* Section 8 */}
          <View style={styles.section}>
            <Text style={styles.heading}>8. Tes droits (RGPD)</Text>
            <Text style={styles.paragraph}>
              Conformément au Règlement Général sur la Protection des Données, tu disposes des droits suivants :
            </Text>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Droit d'accès : obtenir une copie de tes données personnelles</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Droit de rectification : corriger des données inexactes ou incomplètes</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Droit à l'effacement : demander la suppression de tes données</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Droit à la portabilité : recevoir tes données dans un format structuré et lisible</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Droit d'opposition : t'opposer au traitement de tes données</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Droit à la limitation : demander la restriction du traitement</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Droit de retirer ton consentement à tout moment</Text>
            </View>
            <Text style={styles.paragraph}>
              Pour exercer tes droits, contacte-nous à : contact@wealfi.co
            </Text>
            <Text style={styles.paragraph}>
              Nous répondrons dans un délai maximum de 30 jours.
            </Text>
            <Text style={styles.paragraph}>
              Tu disposes également du droit d'introduire une réclamation auprès de la CNIL (Commission Nationale de l'Informatique et des Libertés) : www.cnil.fr
            </Text>
          </View>

          {/* Section 9 */}
          <View style={styles.section}>
            <Text style={styles.heading}>9. Cookies et traceurs</Text>
            <Text style={styles.paragraph}>
              Wealfi utilise des cookies et traceurs pour :
            </Text>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Cookies essentiels : authentification, préférences de session (base légale : exécution du contrat)</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Cookies analytiques : mesure d'audience anonymisée (base légale : consentement)</Text>
            </View>
            <Text style={styles.paragraph}>
              Tu peux gérer tes préférences de cookies depuis les paramètres de l'application. Le refus des cookies analytiques n'affecte pas le fonctionnement du service.
            </Text>
          </View>

          {/* Section 10 */}
          <View style={styles.section}>
            <Text style={styles.heading}>10. Transferts hors UE</Text>
            <Text style={styles.paragraph}>
              Certains de nos sous-traitants (Stripe, Anthropic) sont situés aux États-Unis. Ces transferts sont encadrés par :
            </Text>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Les clauses contractuelles types (CCT) de la Commission européenne</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Les mesures de sécurité complémentaires conformes aux recommandations du CEPD</Text>
            </View>
            <Text style={styles.paragraph}>
              Nous nous assurons que tes données bénéficient d'un niveau de protection équivalent à celui garanti par le RGPD.
            </Text>
          </View>

          {/* Section 11 */}
          <View style={styles.section}>
            <Text style={styles.heading}>11. Profilage et décisions automatisées</Text>
            <Text style={styles.paragraph}>
              Le diagnostic financier de Wealfi constitue un profilage au sens de l'article 22 du RGPD. Ce profilage :
            </Text>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Est basé sur les données que tu nous fournis (déclaratives ou bancaires)</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Produit des analyses et suggestions automatisées</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• N'entraîne pas de décision produisant des effets juridiques à ton égard</Text>
            </View>
            <Text style={styles.paragraph}>
              Tu peux à tout moment :
            </Text>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Contester les résultats du diagnostic</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Demander une intervention humaine</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.paragraph}>• Exprimer ton point de vue</Text>
            </View>
            <Text style={styles.paragraph}>
              en nous contactant à contact@wealfi.co.
            </Text>
          </View>

          {/* Section 12 */}
          <View style={styles.section}>
            <Text style={styles.heading}>12. Modification de la politique</Text>
            <Text style={styles.paragraph}>
              Nous pouvons modifier la présente politique de confidentialité à tout moment. En cas de modification substantielle, tu seras informé(e) par email ou par notification dans l'application. La date de dernière mise à jour est indiquée en haut de cette page.
            </Text>
          </View>

        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Pressable onPress={handleGoBack}>
            <Text style={styles.footerLink}>← Retour à l'accueil</Text>
          </Pressable>
          <Text style={styles.footerCopyright}>© 2026 Wealfi. Tous droits réservés.</Text>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: brandColors.background,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingTop: 70,
  },
  content: {
    paddingHorizontal: 20,
    paddingVertical: 32,
  },

  // Navbar
  navbar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: 'rgba(255,255,255,0.95)',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  navbarDesktop: {
    paddingHorizontal: 48,
    paddingVertical: 16,
  },
  backButton: {
    minWidth: 80,
  },
  backButtonText: {
    fontSize: 15,
    fontWeight: '500',
    color: brandColors.green,
  },
  logoContainer: {
    flex: 1,
    alignItems: 'center',
  },
  logoText: {
    fontSize: 20,
    fontWeight: '700',
    color: brandColors.dark,
  },
  logoTextAccent: {
    color: brandColors.green,
  },

  // Page header
  pageTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: brandColors.dark,
    marginBottom: 8,
  },
  updateDate: {
    fontSize: 14,
    color: brandColors.gray,
    marginBottom: 32,
  },

  // Sections
  section: {
    marginBottom: 32,
  },
  heading: {
    fontSize: 20,
    fontWeight: '700',
    color: brandColors.dark,
    marginBottom: 12,
  },
  paragraph: {
    fontSize: 15,
    color: brandColors.dark,
    lineHeight: 24,
    marginBottom: 8,
  },
  bulletItem: {
    paddingLeft: 8,
  },

  // Table styles
  tableContainer: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    overflow: 'hidden',
    marginTop: 8,
    marginBottom: 8,
  },
  tableRow: {
    flexDirection: 'row',
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: brandColors.white,
  },
  tableRowAlt: {
    backgroundColor: brandColors.background,
  },
  tableCell: {
    paddingHorizontal: 4,
  },
  tableCellHeader: {
    fontSize: 13,
    fontWeight: '700',
    color: brandColors.greenDark,
  },
  tableCellLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: brandColors.dark,
  },
  tableCellValue: {
    fontSize: 14,
    color: brandColors.gray,
    lineHeight: 20,
  },

  // Footer
  footer: {
    backgroundColor: brandColors.dark,
    paddingHorizontal: 20,
    paddingVertical: 32,
    alignItems: 'center',
    gap: 12,
  },
  footerLink: {
    fontSize: 15,
    fontWeight: '500',
    color: brandColors.green,
  },
  footerCopyright: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.5)',
  },
});
